<template>
  <section class="standings-section" id="standings">
    <!-- Pull-to-Refresh Indicator - Enhanced Animation -->
    <div 
      v-if="pullDistance > 0 || refreshing" 
      class="pull-indicator"
      :style="{ 
        opacity: refreshing ? 1 : Math.min(pullDistance / pullThreshold, 1),
        transform: refreshing ? 'translateY(0) scale(1)' : `translateY(${Math.min(pullDistance * 0.3, 30)}px) scale(${Math.min(0.5 + (pullDistance / pullThreshold) * 0.5, 1)})`
      }"
    >
      <div class="pull-circle" :class="{ 'pulling': pullDistance > 0 && !refreshing, 'refreshing': refreshing }">
        <Icon 
          name="mdi:loading" 
          class="pull-icon"
          :class="{ 'spin': refreshing }"
        />
      </div>
    </div>

    <div 
      ref="sectionContainer"
      class="section-container"
      :style="{ transform: pullDistance > 0 && !refreshing ? `translateY(${Math.min(pullDistance * 0.5, 60)}px)` : 'translateY(0)' }"
    >
      <div class="section-header">
        <h2 class="section-title">League Standings & Statistics</h2>
        <p class="section-subtitle">Real-time data for all divisions and players</p>
      </div>

      <div class="controls-panel">
        <div class="division-selector">
          <div class="selector-container">
            <Icon name="mdi:tournament" class="selector-icon" />
            <div class="selector-content">
              <label for="division-select" class="selector-label">Division</label>
              <select 
                id="division-select" 
                v-model="selectedDivisionId" 
                @change="onDivisionChange" 
                :disabled="loadingDivisions || divisions.length === 0"
                class="division-select"
              >
                <option v-if="loadingDivisions" value="">Loading divisions...</option>
                <option v-else-if="!loadingDivisions && divisions.length === 0" value="">No divisions found</option>
                <option v-for="division in divisions" :key="division.id" :value="division.id">
                  {{ division.name }}
                </option>
              </select>
            </div>
          </div>
        </div>
        
        <div v-if="selectedDivisionId && divisions.length > 0" class="data-tabs">
          <button 
            @click="selectedDataType = 'teams'" 
            :class="{ active: selectedDataType === 'teams' }"
            class="tab-button"
          >
            <Icon name="mdi:trophy" />
            <span>Team Standings</span>
          </button>
          <button 
            @click="selectedDataType = 'playerStats'" 
            :class="{ active: selectedDataType === 'playerStats' }"
            class="tab-button"
          >
            <Icon name="mdi:account-star" />
            <span>Player Stats</span>
          </button>
          <button 
            @click="selectedDataType = 'playerTeamStats'" 
            :class="{ active: selectedDataType === 'playerTeamStats' }"
            class="tab-button"
          >
            <Icon name="mdi:account-group" />
            <span>Players/Team</span>
          </button>
          <button 
            @click="selectedDataType = 'playerList'" 
            :class="{ active: selectedDataType === 'playerList' }"
            class="tab-button"
          >
            <Icon name="mdi:format-list-numbered" />
            <span>Player List</span>
          </button>
          <button 
            @click="selectedDataType = 'schedule'" 
            :class="{ active: selectedDataType === 'schedule' }"
            class="tab-button"
          >
            <Icon name="mdi:calendar-month" />
            <span>Schedule</span>
          </button>
        </div>
      </div>

      <!-- Player Search Section -->
      <div v-if="selectedDivisionId" class="player-search-section">
        <div class="search-container">
          <Icon name="mdi:magnify" class="search-icon" />
          <input
            v-model="playerSearchQuery"
            type="text"
            placeholder="Search players by name..."
            class="search-input"
            @input="handlePlayerSearch"
          />
          <button 
            v-if="playerSearchQuery" 
            @click="clearPlayerSearch" 
            class="clear-button"
            aria-label="Clear search"
          >
            <Icon name="mdi:close" />
          </button>
        </div>
        
        <!-- Search Results -->
        <div v-if="playerSearchResults.length > 0 && playerSearchQuery" class="search-results">
          <div class="results-header">
            <Icon name="mdi:account-search" />
            <span>Found {{ playerSearchResults.length }} player{{ playerSearchResults.length !== 1 ? 's' : '' }}</span>
          </div>
          <div class="results-list">
            <div 
              v-for="player in playerSearchResults" 
              :key="player.playerId"
              class="player-result-card"
              @click="openPlayerDetail(player)"
            >
              <div class="player-info">
                <h4 class="player-name">
                  <Icon name="mdi:account" />
                  {{ player.playerName }}
                </h4>
                <div class="player-meta">
                  <span v-if="player.divisions[0]?.teamName" class="team-badge">
                    <Icon name="mdi:shield" />
                    {{ player.divisions[0].teamName }}
                  </span>
                  <span v-if="player.divisions[0]?.fargoRate" class="fargo-badge">
                    <Icon name="mdi:chart-line" />
                    Fargo: {{ player.divisions[0].fargoRate }}
                  </span>
                </div>
              </div>
              <div class="player-stats-summary">
                <div v-if="getPlayerDivisionStats(player)" class="stat-item">
                  <span class="stat-label">Win %:</span>
                  <span class="stat-value">{{ getPlayerDivisionStats(player)?.winPercentage }}%</span>
                </div>
                <div v-if="getPlayerDivisionStats(player)" class="stat-item">
                  <span class="stat-label">W/L:</span>
                  <span class="stat-value">{{ getPlayerDivisionStats(player)?.gamesWon || 0 }}/{{ getPlayerDivisionStats(player)?.gamesLost || 0 }}</span>
                </div>
                <div class="stat-item">
                  <span class="stat-label">Matches:</span>
                  <span class="stat-value">{{ getPlayerMatches(player).length }}</span>
                </div>
              </div>
              <Icon name="mdi:chevron-right" class="card-arrow" />
            </div>
          </div>
        </div>
        
        <!-- No Results Message -->
        <div v-if="playerSearchResults.length === 0 && playerSearchQuery && !indexingPlayers" class="no-results">
          <Icon name="mdi:account-alert" />
          <p>No players found matching "{{ playerSearchQuery }}"</p>
        </div>
      </div>

      <!-- Player Detail Modal -->
      <PlayerDetailModal
        :is-open="playerDetailModalOpen"
        :player-data="selectedPlayerForDetail"
        @close="closePlayerDetail"
      />

      <div v-if="loadingData" class="loading-state">
        <!-- Mobile: Show skeleton cards -->
        <div class="skeleton-container mobile-skeletons">
          <div class="data-header">
            <Icon name="mdi:loading" class="loading-icon" />
            <h3>Loading {{ getDataTypeLabel() }}...</h3>
          </div>
          <div class="skeleton-cards">
            <SkeletonCard v-for="n in 5" :key="n" />
          </div>
        </div>
        
        <!-- Desktop: Show skeleton table -->
        <div class="skeleton-container desktop-skeletons">
          <div class="data-header">
            <Icon name="mdi:loading" class="loading-icon" />
            <h3>Loading {{ getDataTypeLabel() }}...</h3>
          </div>
          <div class="table-container">
            <table class="modern-table">
              <thead>
                <tr>
                  <th v-for="n in getColumnCount()" :key="n">
                    <div class="skeleton-line header-skeleton"></div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <SkeletonTableRow 
                  v-for="n in 8" 
                  :key="n" 
                  :columns="getColumnCount()" 
                />
              </tbody>
            </table>
          </div>
        </div>
      </div>
      
      <div v-if="error" class="status-message error">
        <Icon name="mdi:alert-circle" />
        <span>{{ error }}</span>
      </div>

      <div v-if="!loadingData && !error && selectedDivisionId" class="data-display">
        <!-- Team Standings Table -->
        <div v-if="selectedDataType === 'teams'" class="data-section">
          <div class="data-header">
            <Icon name="mdi:trophy" />
            <h3>Team Standings</h3>
          </div>
          <div v-if="teamStandings && teamStandings.length > 0" class="table-container">
            <table class="modern-table">
              <thead>
                <tr>
                  <th class="rank-col">
                    <Icon name="mdi:podium" />
                    #
                  </th>
                  <th class="team-col">
                    <Icon name="mdi:account-group" />
                    TEAM
                  </th>
                  <th class="points-col">
                    <Icon name="mdi:star" />
                    PTS
                  </th>
                  <th class="wl-col">
                    <Icon name="mdi:gamepad-variant" />
                    W/L
                  </th>
                  <th class="wks-col">
                    <Icon name="mdi:check-circle" />
                    WKs
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(team, index) in teamStandings" :key="team.id" :class="{ 'top-team': index < 3 }">
                  <td class="rank-cell" data-label="Rank">
                    <div class="rank-badge" :class="`rank-${index + 1}`">
                      {{ team.rank }}
                    </div>
                  </td>
                  <td class="team-cell" data-label="Team">
                    <div class="team-name">{{ team.teamName }}</div>
                  </td>
                  <td class="points-cell" data-label="Points">
                    <div class="points-badge">{{ team.points || '-' }}</div>
                  </td>
                  <td class="wl-cell" data-label="W/L">{{ team.wins }}</td>
                  <td class="wks-cell" data-label="Weeks">{{ team.weeks }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="no-data">
            <Icon name="mdi:trophy-broken" />
            <p>No team standings data available for {{ selectedDivisionName }}</p>
          </div>
        </div>

        <!-- Player Stats Table -->
        <div v-if="selectedDataType === 'playerStats'" class="data-section">
          <div class="data-header">
            <Icon name="mdi:account-star" />
            <h3>Player Statistics</h3>
          </div>
          <div v-if="playerStats && playerStats.length > 0" class="table-container">
            <table class="modern-table">
              <thead>
                <tr>
                  <th class="rank-col">
                    <Icon name="mdi:podium" />
                    #
                  </th>
                  <th class="player-col">
                    <Icon name="mdi:account" />
                    NAME
                  </th>
                  <th>
                    <Icon name="mdi:chart-line" />
                    PTS F/A
                  </th>
                  <th>
                    <Icon name="mdi:gamepad-variant" />
                    W/L
                  </th>
                  <th class="percentage-col">
                    <Icon name="mdi:percent" />
                    WIN PCT
                  </th>
                  <th>WKs</th>
                  <th>BRs</th>
                  <th>WZs</th>
                  <th>TRs</th>
                  <th>WBs</th>
                  <th>CWs</th>
                  <th>PMs</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(player, index) in playerStats" :key="player.id" :class="{ 'top-player': index < 3 }">
                  <td class="rank-cell" data-label="Rank">
                    <div class="rank-badge" :class="`rank-${index + 1}`">
                      {{ index + 1 }}
                    </div>
                  </td>
                  <td class="player-cell" data-label="Player">
                    <div class="player-name">{{ player.playerName }}</div>
                  </td>
                  <td data-label="PTS F/A">{{ player.pointsForAgainst }}</td>
                  <td data-label="W/L">{{ player.gamesWon }}</td>
                  <td class="percentage-cell" data-label="Win %">
                    <div class="percentage-badge">{{ player.winPercentage }}%</div>
                  </td>
                  <td data-label="WKs">{{ player.weeks }}</td>
                  <td data-label="BRs">{{ player.breakAndRuns }}</td>
                  <td data-label="WZs">{{ player.winZips }}</td>
                  <td data-label="TRs">{{ player.tableRuns }}</td>
                  <td data-label="WBs">{{ player.winOnBreak }}</td>
                  <td data-label="CWs">{{ player.consecutiveWins }}</td>
                  <td data-label="PMs">{{ player.perfectMatches }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="no-data">
            <Icon name="mdi:account-off" />
            <p>No player statistics available for {{ selectedDivisionName }}</p>
          </div>
        </div>

        <!-- Players/Team Stats Table -->
        <div v-if="selectedDataType === 'playerTeamStats'" class="data-section">
          <div class="data-header">
            <Icon name="mdi:account-group" />
            <h3>Players per Team Statistics</h3>
          </div>
          <div v-if="playerTeamStats && playerTeamStats.length > 0" class="table-container">
            <table class="modern-table detailed-stats">
              <thead>
                <tr>
                  <th>Team</th>
                  <th>Player</th>
                  <th>PTS F/A</th>
                  <th>W/L</th>
                  <th>WIN PCT</th>
                  <th>WKs</th>
                  <th>BRs</th>
                  <th>WZs</th>
                  <th>TRs</th>
                  <th>WBs</th>
                  <th>CWs</th>
                  <th>PMs</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(stat, index) in playerTeamStats" :key="stat.id" :class="{ 'alternate-team': alternatingTeamRows[stat.teamName] }">
                  <td class="team-cell" data-label="Team">{{ stat.teamName }}</td>
                  <td class="player-cell" data-label="Player">{{ stat.playerName }}</td>
                  <td data-label="PTS F/A">{{ stat.pointsForAgainst }}</td>
                  <td data-label="W/L">{{ stat.gamesWon }}</td>
                  <td class="percentage-cell" data-label="Win %">{{ stat.winPercentage }}%</td>
                  <td data-label="WKs">{{ stat.weeks }}</td>
                  <td data-label="BRs">{{ stat.breakAndRuns }}</td>
                  <td data-label="WZs">{{ stat.winZips }}</td>
                  <td data-label="TRs">{{ stat.tableRuns }}</td>
                  <td data-label="WBs">{{ stat.winOnBreak }}</td>
                  <td data-label="CWs">{{ stat.consecutiveWins }}</td>
                  <td data-label="PMs">{{ stat.perfectMatches }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="no-data">
            <Icon name="mdi:account-group-outline" />
            <p>No players/team data available for {{ selectedDivisionName }}</p>
          </div>
        </div>

        <!-- Player List Table -->
        <div v-if="selectedDataType === 'playerList'" class="data-section">
          <div class="data-header">
            <Icon name="mdi:format-list-numbered" />
            <h3>Player List</h3>
          </div>
          <div v-if="playerList && playerList.length > 0" class="table-container">
            <table class="modern-table player-list-table">
              <thead>
                <tr>
                  <th class="player-col">
                    <Icon name="mdi:account" />
                    Player
                  </th>
                  <th class="fargo-col">
                    <Icon name="mdi:chart-line" />
                    Fargo Rate
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="player in playerList" :key="player.id">
                  <td class="player-cell" data-label="Player">
                    <div class="player-name">{{ player.playerName }}</div>
                  </td>
                  <td class="fargo-cell" data-label="Fargo Rate">
                    <div class="fargo-badge">{{ player.fargoRate }}</div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="no-data">
            <Icon name="mdi:format-list-bulleted-square" />
            <p>No player list data available for {{ selectedDivisionName }}</p>
          </div>
        </div>

        <!-- Schedule Table -->
        <div v-if="selectedDataType === 'schedule'" class="data-section">
          <div class="data-header">
            <Icon name="mdi:calendar-month" />
            <h3>Schedule</h3>
          </div>
          <div v-if="schedule && schedule.length > 0" class="table-container">
            <table class="modern-table schedule-table">
              <thead>
                <tr>
                  <th>
                    <Icon name="mdi:calendar" />
                    Date
                  </th>
                  <th>
                    <Icon name="mdi:clock" />
                    Time
                  </th>
                  <th>
                    <Icon name="mdi:map-marker" />
                    Location
                  </th>
                  <th>
                    <Icon name="mdi:home" />
                    Home Team
                  </th>
                  <th>
                    <Icon name="mdi:airplane" />
                    Away Team
                  </th>
                  <th>
                    <Icon name="mdi:chart-box" />
                    Match Results
                  </th>
                  <th>
                    <Icon name="mdi:file-document" />
                    LMS Details
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in schedule" :key="item.id">
                  <td class="date-cell" data-label="Date">{{ item.date }}</td>
                  <td class="time-cell" data-label="Time">7:00PM</td>
                  <td class="location-cell" data-label="Location">{{ item.location }}</td>
                  <td class="team-cell home" data-label="Home Team">{{ item.homeTeam }}</td>
                  <td class="team-cell away" data-label="Away Team">{{ item.awayTeam }}</td>
                  <td class="details-cell" data-label="Match Results">
                    <NuxtLink 
                      v-if="item.matchId && !item.matchLink?.includes('GenerateRosters')" 
                      :to="`/match/${item.matchId}`"
                      class="view-link internal-link"
                    >
                      <Icon name="mdi:trophy" />
                      Results
                    </NuxtLink>
                    <span v-else class="no-link">-</span>
                  </td>
                  <td class="details-cell" data-label="LMS Details">
                    <a 
                      v-if="item.matchLink && !item.matchLink.includes('GenerateRosters')" 
                      :href="item.matchLink" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      class="view-link external-link"
                    >
                      <Icon name="mdi:eye" />
                      View
                    </a>
                    <span v-else class="no-link">-</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="no-data">
            <Icon name="mdi:calendar-remove" />
            <p>No schedule data available for {{ selectedDivisionName }}</p>
          </div>
        </div>
      </div>
      
      <div v-if="!loadingData && !error && !selectedDivisionId && !loadingDivisions && divisions.length === 0" class="no-data">
        <Icon name="mdi:database-off" />
        <p>Please select a division to view data, or wait for divisions to load if none are available.</p>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed, watch } from 'vue';
import { collection, getDocs, query, where, orderBy, type Firestore } from 'firebase/firestore';
import { useNuxtApp } from '#app';
import { usePlayerIndex, type PlayerIndex } from '~/composables/usePlayerIndex';

const LEAGUE_ID = "abdd3e6f-55de-4d1f-8be0-b2d40139058f";

// Template ref for section container
const sectionContainer = ref<HTMLElement | null>(null);

// Player indexing composable
const {
  buildPlayerIndex,
  searchPlayers,
  loading: indexingPlayers,
  playerIndex
} = usePlayerIndex(LEAGUE_ID);

interface Division {
  id: string;
  name: string;
}

interface TeamStanding {
  id: string;
  rank: string;
  teamName: string;
  points: string;
  wins: string;
  weeks: string;
  divisionId: string;
  divisionName: string;
  leagueId: string;
}

interface PlayerStat {
  id: string;
  rank: string;
  playerName: string;
  winPercentage: string;
  gamesWon: string;           // Firestore: gamesWon (not gmsWon)
  pointsForAgainst: string;   // Firestore: pointsForAgainst (not ptsFA)
  weeks: string;              // Firestore: weeks (not wks)
  breakAndRuns: string;       // Firestore: breakAndRuns (not brs)
  winZips: string;            // Firestore: winZips (not wzs)
  tableRuns: string;          // Firestore: tableRuns (not trs)
  winOnBreak: string;         // Firestore: winOnBreak (not wbs)
  consecutiveWins: string;    // Firestore: consecutiveWins (not cws)
  perfectMatches: string;     // Firestore: perfectMatches (not pms)
  divisionId: string;
  divisionName: string;
  leagueId: string;
}

interface PlayerTeamStat {
  id: string;
  playerName: string;
  winPercentage?: string;        // Firestore: winPercentage (not winPct)
  pointsForAgainst?: string;     // Firestore: pointsForAgainst (not ptsFA)
  gamesWon?: string;             // Firestore: gamesWon (not gmsWon)
  weeks?: string;                // Firestore: weeks (not wks)
  breakAndRuns?: string;         // Firestore: breakAndRuns (not brs)
  winZips?: string;              // Firestore: winZips (not wzs)
  tableRuns?: string;            // Firestore: tableRuns (not trs)
  winOnBreak?: string;           // Firestore: winOnBreak (not wbs)
  consecutiveWins?: string;      // Firestore: consecutiveWins (not cws)
  perfectMatches?: string;       // Firestore: perfectMatches (not pms)
  teamName: string; 
  divisionId: string;
  divisionName: string;
  leagueId: string;
}

interface PlayerListEntry {
  id: string;
  playerName: string;
  fargoRate: string;
  divisionId: string;
  divisionName: string;
  leagueId: string;
}

interface ScheduleItem {
  id: string;
  date: string;
  time: string;
  location: string;
  homeTeam: string;
  awayTeam: string;
  result: string;
  matchLink: string | null;
  matchId: string | null;
  divisionId: string;
  divisionName: string;
  leagueId: string;
}

let dbInstance: Firestore | null = null;

const divisions = ref<Division[]>([]);
const selectedDivisionId = ref<string>('');
const loadingDivisions = ref(true);
const loadingData = ref(false);
const error = ref<string | null>(null);
const selectedDataType = ref<'teams' | 'playerStats' | 'playerTeamStats' | 'playerList' | 'schedule'>('teams');

const teamStandings = ref<TeamStanding[]>([]);
const playerStats = ref<PlayerStat[]>([]);
const playerTeamStats = ref<PlayerTeamStat[]>([]);
const playerList = ref<PlayerListEntry[]>([]);
const schedule = ref<ScheduleItem[]>([]);

// Player search state
const playerSearchQuery = ref('');
const playerSearchResults = ref<PlayerIndex[]>([]);

// Player detail modal state
const playerDetailModalOpen = ref(false);
const selectedPlayerForDetail = ref<PlayerIndex | null>(null);

const selectedDivisionName = computed(() => {
  return divisions.value.find(d => d.id === selectedDivisionId.value)?.name || '';
});

const alternatingTeamRows = computed(() => {
  const uniqueTeams = [...new Set(playerTeamStats.value.map(stat => stat.teamName))];
  const teamAlternation: { [key: string]: boolean } = {};
  
  uniqueTeams.forEach((teamName, index) => {
    teamAlternation[teamName] = index % 2 === 1; // Every other team gets gold background
  });
  
  return teamAlternation;
});

// Pull-to-refresh state
const pullDistance = ref(0);
const pullStartY = ref(0);
const isPulling = ref(false);
const refreshing = ref(false);
const pullThreshold = 80; // Distance required to trigger refresh
const lastRefreshTime = ref<string>('');

const fetchDivisions = async () => {
  if (!dbInstance) {
    error.value = 'Firestore is not initialized.';
    loadingDivisions.value = false;
    return;
  }
  loadingDivisions.value = true;
  error.value = null;
  divisions.value = [];
  try {
    const teamsCollectionPath = `leagueStandings/${LEAGUE_ID}/teams`;
    const teamsQuery = query(collection(dbInstance, teamsCollectionPath));
    const querySnapshot = await getDocs(teamsQuery);
    
    const discoveredDivisionsMap = new Map<string, string>();
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      if (data.divisionId && data.divisionName) {
        discoveredDivisionsMap.set(data.divisionId, data.divisionName);
      }
    });

    const sortedDivisions = Array.from(discoveredDivisionsMap).map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
    
    divisions.value = sortedDivisions;
    
    if (divisions.value.length > 0) {
      selectedDivisionId.value = divisions.value[0].id;
    } else {
      error.value = "No divisions found. Data might not be available yet or the league ID is incorrect.";
      selectedDivisionId.value = '';
    }

  } catch (e: any) {
    console.error("Error fetching divisions:", e);
    error.value = "Failed to load division list. Check console for details.";
  } finally {
    loadingDivisions.value = false;
  }
};

const fetchDataForDivision = async (divisionId: string, dataType: string) => {
  if (!dbInstance || !divisionId) {
    teamStandings.value = [];
    playerStats.value = [];
    playerTeamStats.value = [];
    playerList.value = [];
    schedule.value = [];
    return;
  }

  loadingData.value = true;
  error.value = null;

  switch (dataType) {
    case 'teams': teamStandings.value = []; break;
    case 'playerStats': playerStats.value = []; break;
    case 'playerTeamStats': playerTeamStats.value = []; break;
    case 'playerList': playerList.value = []; break;
    case 'schedule': schedule.value = []; break;
  }

  const collectionName = dataType;
  const fullCollectionPath = `leagueStandings/${LEAGUE_ID}/${collectionName}`;

  try {
    let dataQuery = query(
      collection(dbInstance, fullCollectionPath),
      where("divisionId", "==", divisionId)
    );

    const querySnapshot = await getDocs(dataQuery);
    
    const fetchedData: any[] = [];
    querySnapshot.forEach((doc) => {
      fetchedData.push({ id: doc.id, ...doc.data() });
    });

    if (dataType === 'schedule') {
      console.log(`Fetched raw schedule data for division ${divisionId}:`, JSON.parse(JSON.stringify(fetchedData)));
    }

    switch (dataType) {
      case 'teams':
        teamStandings.value = fetchedData.sort((a, b) => parseInt(a.rank || '999') - parseInt(b.rank || '999')) as TeamStanding[];
        break;
      case 'playerStats':
        // Sort by points for (descending), then by win percentage (descending)
        playerStats.value = (fetchedData as PlayerStat[]).sort((a, b) => {
          // Parse points from pointsForAgainst string (e.g., "15/10" -> 15)
          const getPointsFor = (pointsStr: string) => {
            if (!pointsStr || pointsStr === '-') return 0;
            const [pointsFor] = pointsStr.split('/').map(n => parseInt(n) || 0);
            return pointsFor;
          };
          
          const aPoints = getPointsFor(a.pointsForAgainst);
          const bPoints = getPointsFor(b.pointsForAgainst);
          
          // First priority: Most points for (descending)
          if (bPoints !== aPoints) return bPoints - aPoints;
          
          // Second priority: Win percentage (descending)
          const aWinPct = parseFloat(a.winPercentage || '0');
          const bWinPct = parseFloat(b.winPercentage || '0');
          return bWinPct - aWinPct;
        });
        break;
      case 'playerTeamStats':
        playerTeamStats.value = fetchedData.sort((a,b) => {
          // First sort by team name
          const teamCompare = a.teamName.localeCompare(b.teamName);
          if (teamCompare !== 0) return teamCompare;
          
          // Within each team, sort by number of wins first, then by win ratio
          const parseWLData = (wlString: string) => {
            if (!wlString || wlString === '0/0' || wlString === '-') return { wins: 0, total: 0, ratio: 0 };
            const [wins, total] = wlString.split('/').map(n => parseInt(n) || 0);
            return { 
              wins, 
              total, 
              ratio: total > 0 ? wins / total : 0 
            };
          };
          
          const aData = parseWLData(a.gamesWon || '');
          const bData = parseWLData(b.gamesWon || '');
          
          // First priority: Most wins (descending)
          if (bData.wins !== aData.wins) return bData.wins - aData.wins;
          
          // Second priority: Best win ratio (descending)
          if (bData.ratio !== aData.ratio) return bData.ratio - aData.ratio;
          
          // Third priority: Player name (ascending)
          return a.playerName.localeCompare(b.playerName);
        }) as PlayerTeamStat[];
        break;
      case 'playerList':
        playerList.value = fetchedData.sort((a,b) => a.playerName.localeCompare(b.playerName)) as PlayerListEntry[];
        break;
      case 'schedule':
        schedule.value = fetchedData.sort((a,b) => {
          const dateA = new Date(a.date).getTime();
          const dateB = new Date(b.date).getTime();
          if (dateA !== dateB) return dateA - dateB;
          return a.homeTeam.localeCompare(b.homeTeam);
        }) as ScheduleItem[];
        break;
    }

  } catch (e: any) {
    console.error(`Error fetching ${dataType} for division ${divisionId}:`, e);
    error.value = `Failed to load ${dataType}. Check console. Firestore indexes might be needed.`;
  } finally {
    loadingData.value = false;
  }
};

const onDivisionChange = () => {
};

watch([selectedDivisionId, selectedDataType], ([newDivisionId, newDataType], [oldDivisionId, oldDataType]) => {
  if (newDivisionId && (newDivisionId !== oldDivisionId || newDataType !== oldDataType)) {
    fetchDataForDivision(newDivisionId, newDataType);
  }
}, { immediate: false });

onMounted(async () => {
  const nuxtApp = useNuxtApp();
  const firebasePlugin = nuxtApp.$firebase as any;

  if (firebasePlugin && firebasePlugin.firestore) {
    dbInstance = firebasePlugin.firestore;
  } else {
    console.error('Firebase Firestore not available from Nuxt plugin during onMounted.');
    error.value = 'Firebase Firestore setup error. Failed to load data.';
    loadingDivisions.value = false;
    loadingData.value = false;
  }

  if (dbInstance) {
    await fetchDivisions(); 
  }
  
  // Add touch event listeners with passive option for pull-to-refresh
  const container = sectionContainer.value;
  if (container) {
    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: false }); // Must be non-passive to preventDefault
    container.addEventListener('touchend', handleTouchEnd, { passive: true });
  }
});

// Cleanup event listeners on unmount
onUnmounted(() => {
  const container = sectionContainer.value;
  if (container) {
    container.removeEventListener('touchstart', handleTouchStart);
    container.removeEventListener('touchmove', handleTouchMove);
    container.removeEventListener('touchend', handleTouchEnd);
  }
});

// Player search functions
const handlePlayerSearch = () => {
  if (!playerSearchQuery.value.trim()) {
    playerSearchResults.value = [];
    return;
  }

  const results = searchPlayers({
    playerName: playerSearchQuery.value,
    divisionId: selectedDivisionId.value
  });

  // Filter out entries without valid player data (excludes team entries)
  playerSearchResults.value = results.filter(player => {
    // Must have stats
    const hasStats = Object.keys(player.stats).length > 0;
    // Must have at least one match
    const hasMatches = Object.keys(player.matches).length > 0;
    // Stats must have valid numeric data
    const stats = getPlayerDivisionStats(player);
    const hasValidStats = stats && typeof stats.gamesWon === 'number' && typeof stats.gamesLost === 'number';
    
    return hasStats && hasMatches && hasValidStats;
  });
};

const clearPlayerSearch = () => {
  playerSearchQuery.value = '';
  playerSearchResults.value = [];
};

const getPlayerDivisionStats = (player: PlayerIndex) => {
  // Get combined stats across ALL divisions for search results
  const allStats = Object.values(player.stats);
  if (allStats.length === 0) return null;
  
  const totalGamesWon = allStats.reduce((sum, stat) => sum + (stat.gamesWon || 0), 0);
  const totalGamesLost = allStats.reduce((sum, stat) => sum + (stat.gamesLost || 0), 0);
  
  const totalGames = totalGamesWon + totalGamesLost;
  const winPercentage = totalGames > 0 ? Math.round((totalGamesWon / totalGames) * 100) : 0;
  
  return {
    ...allStats[0], // Include other fields from first stat
    winPercentage,
    gamesWon: totalGamesWon,
    gamesLost: totalGamesLost
  };
};

const getPlayerMatches = (player: PlayerIndex) => {
  // Return ALL matches across all divisions for search results
  return player.matches;
};

// Player detail modal functions
const openPlayerDetail = (player: PlayerIndex) => {
  selectedPlayerForDetail.value = player;
  playerDetailModalOpen.value = true;
};

const closePlayerDetail = () => {
  playerDetailModalOpen.value = false;
  selectedPlayerForDetail.value = null;
};

// Watch for division changes to rebuild player index
watch(selectedDivisionId, async (newDivisionId) => {
  if (newDivisionId && playerIndex.value.size === 0) {
    // Build index for all divisions (not filtered by division)
    // This ensures search results have complete data across all divisions
    await buildPlayerIndex();
  }
  // Clear search when division changes
  clearPlayerSearch();
});

// Helper functions for skeleton loaders
const getDataTypeLabel = () => {
  const labels = {
    teams: 'Team Standings',
    playerStats: 'Player Statistics',
    playerTeamStats: 'Players per Team',
    playerList: 'Player List',
    schedule: 'Schedule'
  };
  return labels[selectedDataType.value] || 'Data';
};

const getColumnCount = () => {
  const counts = {
    teams: 5,
    playerStats: 12,
    playerTeamStats: 12,
    playerList: 2,
    schedule: 6
  };
  return counts[selectedDataType.value] || 5;
};

// Pull-to-refresh handlers
const handleTouchStart = (e: TouchEvent) => {
  // Only allow pull-to-refresh when scrolled to top
  const scrollTop = window.scrollY || document.documentElement.scrollTop;
  if (scrollTop === 0 && !refreshing.value && !loadingData.value) {
    pullStartY.value = e.touches[0].clientY;
    isPulling.value = true;
  }
};

const handleTouchMove = (e: TouchEvent) => {
  if (!isPulling.value || refreshing.value || loadingData.value) return;
  
  const currentY = e.touches[0].clientY;
  const diff = currentY - pullStartY.value;
  
  // Only track downward pulls
  if (diff > 0) {
    // Add resistance to the pull (diminishing returns)
    pullDistance.value = Math.min(diff * 0.5, 120);
    
    // Prevent default scrolling when pulling
    if (pullDistance.value > 10) {
      e.preventDefault();
    }
  }
};

const handleTouchEnd = async () => {
  if (!isPulling.value) return;
  
  isPulling.value = false;
  
  // Trigger refresh if pulled past threshold
  if (pullDistance.value >= pullThreshold && !refreshing.value && !loadingData.value) {
    await performRefresh();
  } else {
    // Reset if not pulled far enough
    pullDistance.value = 0;
  }
};

const performRefresh = async () => {
  if (!selectedDivisionId.value || refreshing.value) return;
  
  refreshing.value = true;
  
  try {
    // Refetch data for current division and data type
    await fetchDataForDivision(selectedDivisionId.value, selectedDataType.value);
    
    // Update last refresh time
    const now = new Date();
    const seconds = Math.floor((Date.now() - now.getTime()) / 1000);
    lastRefreshTime.value = 'just now';
    
    // Update timestamp to show relative time
    setTimeout(() => {
      const elapsed = Math.floor((Date.now() - now.getTime()) / 1000);
      if (elapsed < 60) {
        lastRefreshTime.value = `${elapsed} seconds ago`;
      } else if (elapsed < 3600) {
        lastRefreshTime.value = `${Math.floor(elapsed / 60)} minutes ago`;
      } else {
        lastRefreshTime.value = `${Math.floor(elapsed / 3600)} hours ago`;
      }
    }, 1000);
  } catch (e) {
    console.error('Refresh failed:', e);
  } finally {
    // Animate back to original position
    setTimeout(() => {
      refreshing.value = false;
      pullDistance.value = 0;
    }, 500);
  }
};

</script>

<style scoped>
.standings-section {
  padding: 80px 0;
  background: 
    linear-gradient(180deg, rgba(15, 23, 42, 0.9) 0%, rgba(30, 58, 138, 0.9) 100%),
    url('/images/billiards-1-1920w.webp');
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  position: relative;
  overflow: hidden;
}

.standings-section::before {
  content: '';
  position: absolute;
  inset: 0;
  background: 
    radial-gradient(circle at 20% 30%, rgba(255, 184, 28, 0.06) 0%, transparent 50%),
    radial-gradient(circle at 80% 70%, rgba(59, 130, 246, 0.06) 0%, transparent 50%);
  z-index: 1;
}

.section-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
  position: relative;
  z-index: 2;
  transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.section-header {
  text-align: center;
  margin-bottom: 64px;
}

.section-title {
  color: var(--gold);
  font-size: 3rem;
  font-family: var(--font-heading);
  font-weight: 700;
  margin-bottom: 16px;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
  position: relative;
}

.section-title::after {
  content: '';
  position: absolute;
  bottom: -12px;
  left: 50%;
  transform: translateX(-50%);
  width: 80px;
  height: 3px;
  background: linear-gradient(90deg, transparent, var(--gold), transparent);
  border-radius: 2px;
}

.section-subtitle {
  color: var(--cream);
  font-size: 1.2rem;
  opacity: 0.9;
  margin-top: 24px;
  line-height: 1.6;
}

/* Controls Panel */
.controls-panel {
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(15px);
  border-radius: 20px;
  padding: 32px;
  border: 1px solid rgba(255, 184, 28, 0.2);
  margin-bottom: 48px;
}

.division-selector {
  margin-bottom: 32px;
}

.selector-container {
  display: flex;
  align-items: center;
  gap: 16px;
  max-width: 400px;
  margin: 0 auto;
}

.selector-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, var(--gold) 0%, var(--light-gold) 100%);
  border-radius: 12px;
  color: var(--navy-blue);
  font-size: 1.5rem;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(255, 184, 28, 0.3);
}

.selector-content {
  flex: 1;
}

.selector-label {
  display: block;
  color: var(--gold);
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.division-select {
  width: 100%;
  padding: 12px 16px;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 184, 28, 0.3);
  border-radius: 8px;
  color: var(--cream);
  font-size: 1rem;
  font-weight: 500;
  transition: all 0.3s ease;
  backdrop-filter: blur(5px);
}

.division-select:focus {
  outline: none;
  border-color: var(--gold);
  box-shadow: 0 0 0 2px rgba(255, 184, 28, 0.2);
}

.division-select option {
  background: var(--navy-blue);
  color: var(--cream);
}

/* Data Tabs */
.data-tabs {
  display: flex;
  gap: 8px;
  justify-content: center;
  flex-wrap: wrap;
}

.tab-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 184, 28, 0.2);
  border-radius: 10px;
  color: var(--cream);
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.tab-button::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 184, 28, 0.1), transparent);
  transition: left 0.6s ease;
}

.tab-button:hover {
  background: rgba(255, 184, 28, 0.1);
  border-color: rgba(255, 184, 28, 0.4);
  transform: translateY(-2px);
}

.tab-button:hover::before {
  left: 100%;
}

.tab-button.active {
  background: linear-gradient(135deg, var(--gold) 0%, var(--light-gold) 100%);
  border-color: var(--gold);
  color: var(--navy-blue);
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(255, 184, 28, 0.3);
}

.tab-button svg {
  font-size: 1.1rem;
}

/* Player Search Section */
.player-search-section {
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(15px);
  border-radius: 20px;
  padding: 32px;
  border: 1px solid rgba(255, 184, 28, 0.2);
  margin-bottom: 48px;
}

.search-container {
  position: relative;
  max-width: 600px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 12px;
}

.search-icon {
  position: absolute;
  left: 16px;
  font-size: 1.4rem;
  color: var(--gold);
  z-index: 1;
  pointer-events: none;
}

.search-input {
  flex: 1;
  padding: 16px 48px 16px 52px;
  background: rgba(0, 0, 0, 0.3);
  border: 2px solid rgba(255, 184, 28, 0.2);
  border-radius: 12px;
  color: var(--cream);
  font-size: 1rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  outline: none;
}

.search-input::placeholder {
  color: rgba(233, 223, 213, 0.5);
}

.search-input:focus {
  border-color: var(--gold);
  background: rgba(0, 0, 0, 0.4);
  box-shadow: 0 0 0 4px rgba(255, 184, 28, 0.1);
}

.clear-button {
  position: absolute;
  right: 12px;
  padding: 8px;
  background: rgba(255, 184, 28, 0.1);
  border: none;
  border-radius: 8px;
  color: var(--gold);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.clear-button:hover {
  background: rgba(255, 184, 28, 0.2);
  transform: scale(1.05);
}

.search-results {
  margin-top: 24px;
}

.results-header {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--gold);
  font-weight: 600;
  margin-bottom: 16px;
  font-size: 1.1rem;
}

.results-header svg {
  font-size: 1.3rem;
}

.results-list {
  display: grid;
  gap: 16px;
}

.player-result-card {
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 184, 28, 0.2);
  border-radius: 12px;
  padding: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  position: relative;
}

.player-result-card:hover {
  border-color: var(--gold);
  background: rgba(0, 0, 0, 0.4);
  transform: translateX(4px);
  box-shadow: 0 4px 20px rgba(255, 184, 28, 0.2);
}

.player-result-card .card-arrow {
  font-size: 1.5rem;
  color: var(--gold);
  opacity: 0.5;
  transition: all 0.3s ease;
}

.player-result-card:hover .card-arrow {
  opacity: 1;
  transform: translateX(4px);
}

.player-info {
  flex: 1;
}

.player-name {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--cream);
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 8px;
}

.player-name svg {
  color: var(--gold);
  font-size: 1.3rem;
}

.player-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.team-badge,
.fargo-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 500;
}

.team-badge {
  background: rgba(59, 130, 246, 0.2);
  border: 1px solid rgba(59, 130, 246, 0.4);
  color: var(--sky-blue);
}

.team-badge svg {
  font-size: 1rem;
}

.fargo-badge {
  background: rgba(255, 184, 28, 0.2);
  border: 1px solid rgba(255, 184, 28, 0.4);
  color: var(--gold);
}

.fargo-badge svg {
  font-size: 1rem;
}

.player-stats-summary {
  display: flex;
  gap: 16px;
  flex-shrink: 0;
}

.stat-item {
  text-align: center;
  min-width: 60px;
}

.stat-label {
  display: block;
  color: rgba(233, 223, 213, 0.7);
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
  margin-bottom: 4px;
  letter-spacing: 0.5px;
}

.stat-value {
  display: block;
  color: var(--gold);
  font-size: 1.1rem;
  font-weight: 700;
}

.no-results {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 48px 24px;
  color: rgba(233, 223, 213, 0.6);
  text-align: center;
}

.no-results svg {
  font-size: 3rem;
  opacity: 0.5;
}

.no-results p {
  font-size: 1.1rem;
  font-style: italic;
}

/* Responsive adjustments for search */
@media (max-width: 768px) {
  .player-result-card {
    flex-direction: column;
    align-items: flex-start;
  }

  .player-stats-summary {
    width: 100%;
    justify-content: space-around;
    padding-top: 12px;
    border-top: 1px solid rgba(255, 184, 28, 0.2);
  }
}

/* Status Messages */
.status-message {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 24px;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 500;
  margin-bottom: 32px;
}

.status-message.loading {
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.3);
  color: var(--sky-blue);
}

.loading-spinner {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.status-message.error {
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: #fca5a5;
}

/* Data Display */
.data-display {
  background: rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 32px;
  border: 1px solid rgba(255, 184, 28, 0.1);
}

.data-section {
  width: 100%;
}

.data-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(255, 184, 28, 0.2);
}

.data-header svg {
  font-size: 1.8rem;
  color: var(--gold);
}

.data-header h3 {
  color: var(--gold);
  font-size: 1.8rem;
  font-family: var(--font-heading);
  font-weight: 600;
  margin: 0;
}

/* Modern Table */
.table-container {
  overflow-x: auto;
  border-radius: 12px;
  border: 1px solid rgba(255, 184, 28, 0.2);
  background: rgba(0, 0, 0, 0.2);
  max-height: 600px;
  overflow-y: auto;
  /* Enhanced mobile scrolling */
  -webkit-overflow-scrolling: touch; /* Smooth momentum scrolling on iOS */
  scroll-behavior: smooth;
  position: relative;
}

/* Scroll hint shadow indicators */
.table-container::before,
.table-container::after {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  width: 30px;
  pointer-events: none;
  z-index: 5;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.table-container::before {
  left: 0;
  background: linear-gradient(to right, rgba(0, 0, 0, 0.3), transparent);
}

.table-container::after {
  right: 0;
  background: linear-gradient(to left, rgba(0, 0, 0, 0.3), transparent);
}

/* Show shadows when scrollable (handled by JS or pure CSS scroll-snap) */
.table-container.can-scroll-left::before {
  opacity: 1;
}

.table-container.can-scroll-right::after {
  opacity: 1;
}

.modern-table {
  width: 100%;
  border-collapse: collapse;
  /* Fluid typography: scales between 0.75rem (mobile) and 0.95rem (desktop) */
  font-size: clamp(0.75rem, 0.6rem + 0.5vw, 0.95rem);
  /* Use auto layout for flexible columns */
  table-layout: auto;
}

/* Column Width Definitions - Responsive with min/max constraints */
.modern-table .rank-col {
  width: clamp(60px, 5vw, 80px);
  min-width: 50px;
}

.modern-table .team-col {
  min-width: 120px;
  max-width: 200px;
  width: auto;
}

.modern-table .player-col {
  min-width: 100px;
  max-width: 180px;
  width: auto;
}

/* Player List specific - smaller player column */
.modern-table.player-list-table .player-col {
  min-width: 150px;
  max-width: 250px;
}

.modern-table.player-list-table .fargo-col {
  min-width: 100px;
  width: auto;
}

.modern-table .wins-col,
.modern-table .losses-col {
  width: clamp(50px, 4vw, 60px);
  min-width: 45px;
}

.modern-table .wl-col {
  width: clamp(60px, 5vw, 80px);
  min-width: 55px;
}

.modern-table .wks-col {
  width: clamp(60px, 5vw, 80px);
  min-width: 55px;
}

.modern-table .points-col {
  width: clamp(60px, 5vw, 80px);
  min-width: 55px;
}

.modern-table .percentage-col {
  width: clamp(70px, 6vw, 90px);
  min-width: 65px;
}

.modern-table .fargo-col {
  width: clamp(90px, 8vw, 120px);
  min-width: 85px;
}

.modern-table th:not(.rank-col):not(.team-col):not(.player-col):not(.wins-col):not(.losses-col):not(.points-col):not(.percentage-col):not(.fargo-col) {
  width: 70px;
}

.modern-table thead {
  background: linear-gradient(135deg, var(--gold) 0%, var(--light-gold) 100%);
  position: sticky;
  top: 0;
  z-index: 10;
}

.modern-table thead tr {
  border: none;
}

.modern-table th {
  /* Responsive padding: smaller on mobile, larger on desktop */
  padding: clamp(10px, 1vw + 8px, 16px) clamp(8px, 0.8vw + 6px, 12px);
  color: var(--navy-blue);
  font-weight: 600;
  /* Responsive font size for headers */
  font-size: clamp(0.75rem, 0.65rem + 0.4vw, 0.9rem);
  text-transform: none;
  letter-spacing: 0.5px;
  border: none;
  white-space: nowrap;
  text-align: center;
  vertical-align: middle;
  background: linear-gradient(135deg, var(--gold) 0%, var(--light-gold) 100%);
}

.modern-table th.team-col {
  text-align: left;
}

.modern-table th.player-col {
  text-align: left;
}

.modern-table th svg {
  margin-right: 6px;
  font-size: 1rem;
  vertical-align: middle;
}

.modern-table tbody tr {
  border-bottom: 1px solid rgba(255, 184, 28, 0.1);
  transition: all 0.3s ease;
}

.modern-table tbody tr:hover {
  background: rgba(255, 184, 28, 0.05);
}

.modern-table tbody tr.top-team,
.modern-table tbody tr.top-player {
  background: rgba(255, 184, 28, 0.08);
}

.modern-table tbody tr.top-team:hover,
.modern-table tbody tr.top-player:hover {
  background: rgba(255, 184, 28, 0.12);
}

.modern-table tbody tr.alternate-team {
  background: rgba(255, 184, 28, 0.05);
}

.modern-table tbody tr.alternate-team:hover {
  background: rgba(255, 184, 28, 0.08);
}

.modern-table td {
  /* Responsive padding for cells */
  padding: clamp(10px, 0.8vw + 8px, 14px) clamp(8px, 0.6vw + 6px, 12px);
  color: var(--cream);
  border: none;
  vertical-align: middle;
  text-align: center;
}

.modern-table td.team-cell {
  text-align: left;
}

.modern-table td.player-cell {
  text-align: left;
}

/* Specialized Cell Styles */
.rank-cell {
  text-align: center;
}

.rank-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  /* Responsive size */
  width: clamp(28px, 2vw + 24px, 32px);
  height: clamp(28px, 2vw + 24px, 32px);
  border-radius: 8px;
  font-weight: 700;
  /* Responsive font size */
  font-size: clamp(0.75rem, 0.65rem + 0.4vw, 0.9rem);
  background: rgba(255, 184, 28, 0.2);
  color: var(--gold);
  border: 1px solid rgba(255, 184, 28, 0.3);
}

.rank-badge.rank-1 {
  background: linear-gradient(135deg, #ffd700 0%, #ffed4a 100%);
  color: #1a202c;
  box-shadow: 0 2px 8px rgba(255, 215, 0, 0.4);
}

.rank-badge.rank-2 {
  background: linear-gradient(135deg, #c0c0c0 0%, #e2e8f0 100%);
  color: #1a202c;
  box-shadow: 0 2px 8px rgba(192, 192, 192, 0.4);
}

.rank-badge.rank-3 {
  background: linear-gradient(135deg, #cd7f32 0%, #d69e2e 100%);
  color: #1a202c;
  box-shadow: 0 2px 8px rgba(205, 127, 50, 0.4);
}

.team-cell,
.player-cell {
  font-weight: 600;
  text-align: left;
}

.team-name,
.player-name {
  color: var(--gold);
  font-weight: 600;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

/* Mobile specific - make names more compact */
@media (max-width: 768px) {
  .team-name,
  .player-name {
    font-size: clamp(0.7rem, 0.6rem + 0.4vw, 0.85rem);
    max-width: 150px;
  }
}

.wins-cell {
  color: #68d391;
  font-weight: 600;
}

.losses-cell {
  color: #f56565;
  font-weight: 600;
}

.wl-cell {
  color: var(--cream);
  font-weight: 600;
  text-align: center;
}

.wks-cell {
  color: var(--cream);
  font-weight: 500;
  text-align: center;
}

.points-cell,
.percentage-cell,
.fargo-cell {
  text-align: center;
}

.points-badge,
.percentage-badge,
.fargo-badge {
  display: inline-block;
  /* Responsive padding */
  padding: clamp(3px, 0.3vw + 2px, 4px) clamp(6px, 0.5vw + 4px, 8px);
  background: rgba(255, 184, 28, 0.2);
  border: 1px solid rgba(255, 184, 28, 0.3);
  border-radius: 6px;
  color: var(--gold);
  font-weight: 600;
  /* Responsive font size */
  font-size: clamp(0.75rem, 0.65rem + 0.4vw, 0.9rem);
}

.diff-cell {
  text-align: center;
  font-weight: 600;
}

.diff-cell.positive {
  color: #68d391;
}

.diff-cell.negative {
  color: #f56565;
}

/* Schedule specific styles */
.schedule-table .team-cell.home {
  color: var(--gold);
}

.schedule-table .team-cell.away {
  color: var(--sky-blue);
}

.date-cell,
.time-cell {
  font-family: monospace;
  font-weight: 500;
}

.view-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--gold);
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.3s ease;
  padding: 4px 8px;
  border-radius: 4px;
}

.view-link:hover {
  color: var(--light-gold);
  background: rgba(251, 191, 36, 0.1);
}

.view-link.internal-link {
  background: rgba(34, 197, 94, 0.1);
  color: #22c55e;
}

.view-link.internal-link:hover {
  background: rgba(34, 197, 94, 0.2);
  color: #4ade80;
}

.view-link.external-link {
  background: rgba(251, 191, 36, 0.1);
  color: var(--gold);
}

.view-link.external-link:hover {
  background: rgba(251, 191, 36, 0.2);
  color: var(--light-gold);
}

.no-link {
  color: var(--cream);
  opacity: 0.5;
}

/* Detailed stats table */
.detailed-stats {
  /* Responsive font size - slightly smaller than main table */
  font-size: clamp(0.7rem, 0.55rem + 0.45vw, 0.85rem);
}

.detailed-stats th,
.detailed-stats td {
  /* Responsive padding for detailed stats */
  padding: clamp(8px, 0.6vw + 6px, 10px) clamp(6px, 0.4vw + 4px, 8px);
}

/* No Data State */
.no-data {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 24px;
  text-align: center;
  color: var(--cream);
  opacity: 0.7;
}

.no-data svg {
  font-size: 4rem;
  color: var(--gold);
  margin-bottom: 16px;
  opacity: 0.6;
}

.no-data p {
  font-size: 1.1rem;
  margin: 0;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .detailed-stats {
    font-size: 0.8rem;
  }
  
  .detailed-stats th,
  .detailed-stats td {
    padding: 8px 6px;
  }
}

@media (max-width: 768px) {
  .standings-section {
    padding: 60px 0;
  }
  
  .section-container {
    padding: 0 16px;
  }
  
  .section-header {
    margin-bottom: 48px;
  }
  
  .section-title {
    font-size: 2.2rem;
  }
  
  .section-subtitle {
    font-size: 1.1rem;
  }
  
  .controls-panel {
    padding: 24px 20px;
  }
  
  .data-tabs {
    gap: 6px;
    overflow-x: auto;
    justify-content: flex-start;
    padding-bottom: 8px;
  }
  
  .tab-button {
    padding: 10px 14px;
    font-size: 0.85rem;
    white-space: nowrap;
    flex-shrink: 0;
  }
  
  .tab-button span {
    font-size: 0.75rem; /* Keep labels visible but smaller */
  }
  
  .data-display {
    padding: 24px 16px;
  }
  
  .modern-table {
    font-size: 0.85rem;
  }
  
  .modern-table th,
  .modern-table td {
    padding: 10px 8px;
  }
  
  .detailed-stats {
    font-size: 0.75rem;
  }
  
  .detailed-stats th,
  .detailed-stats td {
    padding: 6px 4px;
  }
}

@media (max-width: 480px) {
  .standings-section {
    padding: 40px 0;
  }
  
  .section-title {
    font-size: 1.8rem;
  }
  
  .controls-panel {
    padding: 20px 16px;
  }
  
  .selector-container {
    flex-direction: column;
    text-align: center;
    gap: 12px;
  }
  
  .data-tabs {
    justify-content: center;
  }
  
  .tab-button {
    padding: 8px 12px;
  }
  
  .data-display {
    padding: 20px 12px;
  }
  
  .data-header {
    flex-direction: column;
    text-align: center;
    gap: 8px;
  }
  
  .modern-table th,
  .modern-table td {
    padding: 8px 4px;
    font-size: 0.8rem;
  }
  
  .rank-badge {
    width: 28px;
    height: 28px;
    font-size: 0.8rem;
  }
  
  .no-data svg {
    font-size: 3rem;
  }
  
  .no-data p {
    font-size: 1rem;
  }
}

/* Mobile Horizontal Scroll Enhancement */
@media (max-width: 768px) {
  .table-container {
    /* Force horizontal scrolling on mobile - keep table layout intact */
    overflow-x: auto !important;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch; /* Smooth iOS scrolling */
    position: relative;
    
    /* Add subtle border to indicate scrollable area */
    border: 2px solid rgba(255, 184, 28, 0.3);
    box-shadow: inset 0 0 20px rgba(0, 0, 0, 0.3);
  }
  
  /* Ensure table maintains its structure on mobile */
  .modern-table {
    /* Remove fixed min-width, let content determine width */
    width: max-content; /* Table expands to fit content */
    min-width: 100%; /* At least full container width */
    display: table; /* Explicitly keep table display */
  }
  
  .modern-table thead {
    display: table-header-group; /* Keep headers visible */
    position: sticky;
    top: 0;
    z-index: 10;
  }
  
  .modern-table tbody {
    display: table-row-group; /* Keep table structure */
  }
  
  .modern-table tr {
    display: table-row; /* Keep row structure */
  }
  
  .modern-table th,
  .modern-table td {
    display: table-cell; /* Keep cell structure */
    white-space: nowrap; /* Prevent text wrapping */
  }
  
  /* Make columns more compact on mobile */
  .modern-table .rank-col {
    width: clamp(45px, 10vw, 60px);
    min-width: 40px;
  }
  
  .modern-table .team-col,
  .modern-table .player-col {
    min-width: 100px;
    max-width: 150px;
  }
  
  .modern-table .wins-col,
  .modern-table .losses-col,
  .modern-table .wl-col,
  .modern-table .wks-col,
  .modern-table .points-col {
    width: clamp(45px, 10vw, 60px);
    min-width: 40px;
  }
  
  .modern-table .percentage-col {
    width: clamp(60px, 12vw, 75px);
    min-width: 55px;
  }
  
  .modern-table .fargo-col {
    width: clamp(75px, 15vw, 100px);
    min-width: 70px;
  }
  
  /* Sticky first column (rank) for context while scrolling */
  .modern-table th:first-child,
  .modern-table td:first-child {
    position: sticky;
    left: 0;
    z-index: 5;
    background: linear-gradient(135deg, var(--gold) 0%, var(--light-gold) 100%);
  }
  
  .modern-table td:first-child {
    background: rgba(0, 0, 0, 0.3);
    backdrop-filter: blur(5px);
  }
  
  /* Add shadow to first column to indicate scroll */
  .modern-table th:first-child::after,
  .modern-table td:first-child::after {
    content: '';
    position: absolute;
    top: 0;
    right: -10px;
    bottom: 0;
    width: 10px;
    background: linear-gradient(to right, rgba(0, 0, 0, 0.2), transparent);
    pointer-events: none;
  }
  
  /* Detailed stats table - more compact */
  .detailed-stats {
    width: max-content;
    min-width: 100%;
  }
}

/* Mobile scroll hint - Add to data-display container on mobile only */
@media (max-width: 768px) {
  .data-display {
    position: relative;
  }
  
  /* Scroll hint overlay - pulses to indicate horizontal scroll */
  .data-section .table-container {
    position: relative;
  }
  
  .data-section .table-container::after {
    content: '→ Scroll →';
    position: absolute;
    bottom: 20px;
    right: 20px;
    background: rgba(255, 184, 28, 0.95);
    color: var(--navy-blue);
    padding: 8px 16px;
    border-radius: 20px;
    font-size: 0.85rem;
    font-weight: 600;
    pointer-events: none;
    z-index: 20;
    box-shadow: 0 4px 12px rgba(255, 184, 28, 0.5);
    animation: scrollHintFade 3s ease-in-out 1s forwards;
    opacity: 1;
    letter-spacing: 1px;
  }
  
  @keyframes scrollHintFade {
    0% {
      opacity: 1;
      transform: translateY(0);
    }
    70% {
      opacity: 1;
      transform: translateY(0);
    }
    100% {
      opacity: 0;
      transform: translateY(-10px);
    }
  }
}

/* Loading State Skeleton Styles */
.loading-state {
  position: relative;
}

.skeleton-container {
  padding: 32px 0;
}

.loading-icon {
  animation: spin 1s linear infinite;
  color: var(--gold);
  font-size: 1.5rem;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.skeleton-cards {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.skeleton-line {
  height: 16px;
  background: rgba(255, 184, 28, 0.2);
  border-radius: 4px;
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.header-skeleton {
  width: 60px;
}

/* Show mobile skeletons on mobile, desktop on desktop */
.mobile-skeletons {
  display: block;
}

.desktop-skeletons {
  display: none;
}

@media (min-width: 769px) {
  .mobile-skeletons {
    display: none;
  }
  
  .desktop-skeletons {
    display: block;
  }
}

/* Pull-to-Refresh Styles - Enhanced Animation */
.pull-indicator {
  position: fixed;
  top: 100px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.3s ease, transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: none;
}

.pull-circle {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.pull-circle.pulling {
  background: rgba(255, 184, 28, 0.15);
  border: 2px solid rgba(255, 184, 28, 0.4);
  box-shadow: 0 4px 16px rgba(255, 184, 28, 0.3);
}

.pull-circle.refreshing {
  background: rgba(255, 184, 28, 0.2);
  border: 2px solid var(--gold);
  box-shadow: 0 4px 20px rgba(255, 184, 28, 0.5);
}

.pull-icon {
  font-size: 1.75rem;
  color: var(--gold);
  transition: transform 0.3s ease;
}

.pull-circle.pulling .pull-icon {
  animation: pulse 1s ease-in-out infinite;
}

.pull-icon.spin {
  animation: spin 1s linear infinite;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
}

/* Only show pull-to-refresh on mobile */
@media (min-width: 769px) {
  .pull-indicator {
    display: none;
  }
}
</style>
